import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, UsersRound, Flag, Award, Megaphone, Camera,
  Trophy, Euro, Plus, Trash2, Check, RefreshCw,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "registrations", label: "Registrations", icon: Users },
  { id: "teams", label: "Teams & Tee Times", icon: UsersRound },
  { id: "scoring", label: "Live Scoring", icon: Flag },
  { id: "sponsors", label: "Sponsors", icon: Award },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "gallery", label: "Gallery", icon: Camera },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-16 max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl text-slate-900">Organiser Dashboard</h1>
            <p className="text-slate-500 text-sm">Signed in as {user?.name}</p>
          </div>
        </div>

        <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${tab === t.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "overview" && <Overview />}
          {tab === "registrations" && <Registrations />}
          {tab === "teams" && <Teams />}
          {tab === "scoring" && <Scoring />}
          {tab === "sponsors" && <Sponsors />}
          {tab === "announcements" && <Announcements />}
          {tab === "gallery" && <Gallery />}
        </div>
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-100 bg-white ${className}`}>{children}</div>;
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="h-32 animate-pulse bg-slate-100 rounded-2xl" />;
  const items = [
    { label: "Registrations", value: stats.registrations, icon: Users, color: "text-sky-600" },
    { label: "Paid entries", value: stats.paid_registrations, icon: Check, color: "text-green-600" },
    { label: "Entry revenue", value: `€${stats.revenue}`, icon: Euro, color: "text-slate-900" },
    { label: "Donations", value: `€${Math.round(stats.donations_total)}`, icon: Trophy, color: "text-green-600" },
    { label: "Sponsors", value: stats.sponsors, icon: Award, color: "text-sky-600" },
    { label: "Teams", value: stats.teams, icon: UsersRound, color: "text-slate-900" },
    { label: "Players registered", value: stats.players, icon: Users, color: "text-sky-600" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((it) => (
        <Card key={it.label} className="p-6">
          <it.icon className={it.color} size={22} />
          <p className="mt-3 font-display text-3xl text-slate-900">{it.value}</p>
          <p className="text-sm text-slate-500">{it.label}</p>
        </Card>
      ))}
    </div>
  );
}

function Registrations() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/registrations").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/registrations/${id}`, { payment_status: status });
    toast.success("Updated"); load();
  };
  const remove = async (id) => { await api.delete(`/registrations/${id}`); toast.success("Removed"); load(); };

  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-slate-900 text-white text-xs uppercase font-semibold">
        <div className="col-span-3">Captain</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-3">Team / Contact</div>
        <div className="col-span-2">Amount</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      {rows.length === 0 && <p className="p-8 text-center text-slate-500">No registrations yet.</p>}
      {rows.map((r) => (
        <div key={r.id} data-testid={`admin-reg-${r.id}`} className="grid grid-cols-12 gap-2 px-5 py-4 items-center border-t border-slate-50 text-sm">
          <div className="col-span-3 font-semibold text-slate-900">{r.captain_name}</div>
          <div className="col-span-2 capitalize text-slate-600">{r.entry_type}</div>
          <div className="col-span-3 text-slate-600">{r.team_name || "—"}<br /><span className="text-xs text-slate-400">{r.email}</span></div>
          <div className="col-span-2 font-semibold">€{r.amount}</div>
          <div className="col-span-2 flex items-center justify-end gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${r.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r.payment_status}</span>
            {r.payment_status !== "paid" && <button onClick={() => setStatus(r.id, "paid")} className="text-green-600 hover:text-green-700" title="Mark paid"><Check size={16} /></button>}
            <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
          </div>
        </div>
      ))}
    </Card>
  );
}

function Teams() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ team_name: "", players: "", tee_time: "", captain_email: "" });
  const load = useCallback(() => api.get("/teams").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.team_name) { toast.error("Team name required"); return; }
    try {
      await api.post("/teams", { team_name: form.team_name, players: form.players.split(",").map((s) => s.trim()).filter(Boolean), tee_time: form.tee_time, tee_hole: "1", captain_email: form.captain_email });
      setForm({ team_name: "", players: "", tee_time: "", captain_email: "" });
      toast.success("Team added"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/teams/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-6 space-y-3 h-fit">
        <h3 className="font-semibold text-slate-900">Add team / pairing</h3>
        <input data-testid="team-name-input" placeholder="Team name" value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input placeholder="Players (comma separated)" value={form.players} onChange={(e) => setForm({ ...form, players: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input data-testid="team-tee-input" placeholder="Tee time e.g. 10:20am" value={form.tee_time} onChange={(e) => setForm({ ...form, tee_time: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input placeholder="Captain email (for scoring)" value={form.captain_email} onChange={(e) => setForm({ ...form, captain_email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <button data-testid="team-add-btn" onClick={add} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-sky-600 text-white font-semibold text-sm hover:-translate-y-0.5 transition-all"><Plus size={16} /> Add team</button>
      </Card>

      <div className="lg:col-span-2 space-y-3">
        {rows.map((t) => (
          <Card key={t.id} data-testid={`admin-team-${t.id}`} className="p-5 flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">{t.team_name} {t.tee_time && <span className="ml-2 text-xs font-normal text-sky-600">⛳ {t.tee_time}</span>}</p>
              <p className="text-sm text-slate-500">{t.players?.join(", ") || "No players"}{t.captain_email && ` · captain: ${t.captain_email}`}</p>
            </div>
            <button onClick={() => remove(t.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
          </Card>
        ))}
        {rows.length === 0 && <p className="text-slate-500 text-sm">No teams yet.</p>}
      </div>
    </div>
  );
}

function Scoring() {
  const [teams, setTeams] = useState([]);
  const [team, setTeam] = useState("");
  const [hole, setHole] = useState(1);
  const [strokes, setStrokes] = useState(4);
  const [board, setBoard] = useState([]);

  const loadBoard = useCallback(() => api.get("/leaderboard").then((r) => setBoard(r.data.rows)), []);
  useEffect(() => {
    api.get("/teams").then((r) => { setTeams(r.data); if (r.data[0]) setTeam(r.data[0].team_name); });
    loadBoard();
  }, [loadBoard]);

  const submit = async () => {
    try {
      await api.post("/scores", { team_name: team, hole: Number(hole), strokes: Number(strokes) });
      toast.success(`Saved hole ${hole} for ${team}`);
      setHole((h) => Math.min(18, h + 1));
      loadBoard();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 space-y-4 h-fit">
        <h3 className="font-semibold text-slate-900">Enter a score</h3>
        <div>
          <label className="text-sm text-slate-600">Team</label>
          <select data-testid="score-team" value={team} onChange={(e) => setTeam(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:ring-2 focus:ring-sky-500">
            {teams.map((t) => <option key={t.id} value={t.team_name}>{t.team_name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-slate-600">Hole</label>
            <input data-testid="score-hole" type="number" min="1" max="18" value={hole} onChange={(e) => setHole(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div>
            <label className="text-sm text-slate-600">Strokes</label>
            <input data-testid="score-strokes" type="number" min="1" value={strokes} onChange={(e) => setStrokes(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
        </div>
        <button data-testid="score-submit" onClick={submit} className="w-full py-2.5 rounded-full bg-green-600 text-white font-semibold hover:-translate-y-0.5 transition-all">Save score</button>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Current standings</h3>
          <button onClick={loadBoard} className="text-slate-400 hover:text-sky-600"><RefreshCw size={16} /></button>
        </div>
        <div className="space-y-2">
          {board.map((r) => (
            <div key={r.team_name} className="flex items-center justify-between text-sm py-2 border-b border-slate-50">
              <span className="font-medium text-slate-800">{r.position}. {r.team_name}</span>
              <span className={`font-bold ${r.to_par < 0 ? "text-green-600" : r.to_par > 0 ? "text-red-500" : "text-slate-600"}`}>
                {r.to_par === 0 ? "E" : r.to_par > 0 ? `+${r.to_par}` : r.to_par} · {r.holes_played}/18
              </span>
            </div>
          ))}
          {board.length === 0 && <p className="text-slate-500 text-sm">No scores yet.</p>}
        </div>
      </Card>
    </div>
  );
}

function Sponsors() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", tier: "gold", website: "", description: "", logo_url: "" });
  const load = useCallback(() => api.get("/sponsors").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    await api.post("/sponsors", form); setForm({ name: "", tier: "gold", website: "", description: "", logo_url: "" }); toast.success("Sponsor added"); load();
  };
  const remove = async (id) => { await api.delete(`/sponsors/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-6 space-y-3 h-fit">
        <h3 className="font-semibold text-slate-900">Add sponsor</h3>
        <input data-testid="sponsor-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <select data-testid="sponsor-tier" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500">
          <option value="platinum">Platinum</option><option value="gold">Gold</option><option value="silver">Silver</option><option value="hole">Hole sponsor</option>
        </select>
        <input placeholder="Logo URL (optional)" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <button data-testid="sponsor-add-btn" onClick={add} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-sky-600 text-white font-semibold text-sm"><Plus size={16} /> Add</button>
      </Card>
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
        {rows.map((s) => (
          <Card key={s.id} className="p-5 flex items-start justify-between">
            <div>
              <span className="text-xs font-semibold uppercase text-sky-600">{s.tier}</span>
              <p className="font-semibold text-slate-900">{s.name}</p>
              <p className="text-sm text-slate-500">{s.description}</p>
            </div>
            <button onClick={() => remove(s.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Announcements() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", channel: "web", urgent: false });
  const load = useCallback(() => api.get("/announcements").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.title || !form.body) { toast.error("Title and body required"); return; }
    await api.post("/announcements", form);
    if (form.channel !== "web") toast.success(`Broadcast queued via ${form.channel} (demo)`);
    else toast.success("Announcement posted");
    setForm({ title: "", body: "", channel: "web", urgent: false }); load();
  };
  const remove = async (id) => { await api.delete(`/announcements/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-6 space-y-3 h-fit">
        <h3 className="font-semibold text-slate-900">New announcement</h3>
        <input data-testid="ann-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <textarea data-testid="ann-body" placeholder="Message" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <select data-testid="ann-channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500">
          <option value="web">Post to website</option>
          <option value="email">Email all participants</option>
          <option value="whatsapp">WhatsApp broadcast</option>
        </select>
        <button data-testid="ann-add-btn" onClick={add} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-sky-600 text-white font-semibold text-sm"><Megaphone size={15} /> Publish</button>
      </Card>
      <div className="lg:col-span-2 space-y-3">
        {rows.map((a) => (
          <Card key={a.id} className="p-5 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900">{a.title}</p>
                {a.channel !== "web" && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{a.channel}</span>}
              </div>
              <p className="text-sm text-slate-500 mt-1">{a.body}</p>
            </div>
            <button onClick={() => remove(a.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Gallery() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ image_url: "", caption: "", year: 2026 });
  const load = useCallback(() => api.get("/gallery").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.image_url) { toast.error("Image URL required"); return; }
    await api.post("/gallery", { ...form, year: Number(form.year) }); setForm({ image_url: "", caption: "", year: 2026 }); toast.success("Added"); load();
  };
  const remove = async (id) => { await api.delete(`/gallery/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-6 space-y-3 h-fit">
        <h3 className="font-semibold text-slate-900">Add photo</h3>
        <input data-testid="gallery-url" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <input type="number" placeholder="Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500" />
        <button data-testid="gallery-add-btn" onClick={add} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-sky-600 text-white font-semibold text-sm"><Plus size={16} /> Add photo</button>
      </Card>
      <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {rows.map((g) => (
          <div key={g.id} className="relative rounded-xl overflow-hidden group">
            <img src={g.image_url} alt={g.caption} className="w-full h-32 object-cover" />
            <button onClick={() => remove(g.id)} className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-white/90 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
